import { computed } from 'vue';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { INBOX_TYPES } from 'dashboard/helper/inbox';

/**
 * WAHA session statuses that represent a real disconnection.
 * Only WORKING = connected. STARTING = transitional (pending, not connected,
 * not disconnected). SCAN_QR_CODE = QR pending (disconnected — session is
 * not usable). STOPPED/FAILED/NOT_FOUND/ERROR = disconnected.
 */
const WAHA_DISCONNECTED_STATUSES = [
  'STOPPED',
  'FAILED',
  'NOT_FOUND',
  'ERROR',
  'SCAN_QR_CODE',
];
const WAHA_CONNECTED_STATUSES = ['WORKING'];
const WAHA_CHANNEL_TYPES = new Set([INBOX_TYPES.API, INBOX_TYPES.WHATSAPP_WEB]);

/**
 * Channel types that support OAuth reauthorization semantics.
 * When `reauthorization_required` is false on these types, the channel
 * is genuinely connected. Types NOT in this set (WebWidget, plain API,
 * plain Email, Telegram, Twilio, SMS, Line) have no connection health
 * concept from the inbox list payload.
 */
const REAUTHORIZABLE_CHANNEL_TYPES = new Set([
  INBOX_TYPES.FB,
  INBOX_TYPES.INSTAGRAM,
  INBOX_TYPES.TIKTOK,
  INBOX_TYPES.WHATSAPP,
]);

/**
 * Tri-state connection status for an inbox.
 *
 * @returns {'connected'|'disconnected'|'not_applicable'}
 *
 * - 'disconnected': reauthorization_required === true (FB, Instagram, TikTok,
 *   WhatsApp Cloud embedded signup, Email OAuth) or WAHA session in a bad
 *   state (STOPPED/FAILED/NOT_FOUND/ERROR) or QR pending (SCAN_QR_CODE).
 * - 'connected': WAHA with WORKING status, or a reauthorizable channel
 *   that is not flagged for reauthorization.
 * - 'not_applicable': channels without connection health semantics
 *   (WebWidget, plain API non-WAHA, plain Email, Telegram, Twilio, SMS, Line),
 *   WAHA inbox whose status was never checked, WAHA STARTING (transitional),
 *   or WhatsApp Cloud manual (no embedded_signup — no health signal).
 */
const inboxConnectionStatus = inbox => {
  if (!inbox) return 'not_applicable';

  // Reauthorization flag — set by backend for OAuth channels (FB, Instagram,
  // TikTok, WhatsApp Cloud embedded signup, Microsoft/Google Email OAuth).
  if (inbox.reauthorization_required === true) return 'disconnected';

  // WAHA (non-official WhatsApp via Channel::Api or Channel::WhatsappWeb)
  if (
    WAHA_CHANNEL_TYPES.has(inbox.channel_type) &&
    inbox.additional_attributes?.waha_session_name
  ) {
    const wahaStatus = inbox.additional_attributes?.waha_status;
    if (wahaStatus && WAHA_DISCONNECTED_STATUSES.includes(wahaStatus)) {
      return 'disconnected';
    }
    // Only WORKING = connected
    if (wahaStatus && WAHA_CONNECTED_STATUSES.includes(wahaStatus)) {
      return 'connected';
    }
    // STARTING, unknown or never-checked — pending, not connected, not alarming
    return 'not_applicable';
  }

  // WhatsApp Cloud: only embedded_signup has a health signal (reauthorization).
  // Manual WhatsApp (no provider_config.source === 'embedded_signup') has no
  // health signal from the inbox list payload.
  if (inbox.channel_type === INBOX_TYPES.WHATSAPP) {
    if (inbox.provider_config?.source === 'embedded_signup') {
      // reauthorization_required === false → genuinely connected
      return 'connected';
    }
    // Manual WhatsApp — no health signal
    return 'not_applicable';
  }

  // Reauthorizable channels that are not flagged → connected
  if (REAUTHORIZABLE_CHANNEL_TYPES.has(inbox.channel_type)) {
    return 'connected';
  }

  // Email OAuth disconnection is caught by reauthorization_required above.
  // Plain Email (IMAP/SMTP) and all other channels have no semantics here.
  return 'not_applicable';
};

/**
 * Convenience boolean wrapper around inboxConnectionStatus.
 * Kept for backward compatibility with existing callers.
 */
const isInboxDisconnected = inbox =>
  inboxConnectionStatus(inbox) === 'disconnected';

/**
 * Returns the list of WAHA inboxes with waha_session_name
 * from the current store records. Used by the refresh logic to poll
 * the real WAHA /status endpoint per inbox.
 */
const wahaInboxes = inboxes =>
  (inboxes || []).filter(
    i =>
      WAHA_CHANNEL_TYPES.has(i.channel_type) &&
      i.additional_attributes?.waha_session_name
  );

/**
 * Composable that derives inbox connection health from the existing store.
 *
 * @returns {{
 *   disconnectedInboxes: import('vue').ComputedRef<Array>,
 *   hasDisconnectedInboxes: import('vue').ComputedRef<boolean>,
 *   disconnectedCount: import('vue').ComputedRef<number>,
 *   canManageInboxes: import('vue').ComputedRef<boolean>,
 *   refreshInboxes: (accountId: number) => Promise<Array|null>,
 *   refreshWahaStatuses: (accountId: number) => Promise<void>,
 * }}
 */
export function useInboxConnectionHealth() {
  const store = useStore();
  const inboxes = useMapGetter('inboxes/getInboxes');
  const { isAdmin } = useAdmin();

  const disconnectedInboxes = computed(() =>
    (inboxes.value || []).filter(isInboxDisconnected)
  );

  const hasDisconnectedInboxes = computed(
    () => disconnectedInboxes.value.length > 0
  );

  const disconnectedCount = computed(() => disconnectedInboxes.value.length);

  // Only administrators can access Settings > Inboxes.
  const canManageInboxes = computed(() => isAdmin.value);

  // Read-only account-scoped refresh. It deliberately does not replace the
  // global inbox store, so a late response from a previous account is harmless.
  const refreshInboxes = accountId =>
    store.dispatch('inboxes/getHealthRecords', accountId);

  // Real WAHA status refresh — calls the existing /waha/status endpoint
  // per WAHA inbox and commits results to the store via updateWahaStatus.
  // Guarded per account: duplicate refreshes for the same account are ignored,
  // while an account switch can start its own refresh without stale IDs.
  const wahaRefreshesInFlight = new Set();

  const refreshWahaStatuses = async (
    accountId,
    sourceInboxes = inboxes.value
  ) => {
    if (!isAdmin.value || wahaRefreshesInFlight.has(accountId)) return;
    const wahaList = wahaInboxes(sourceInboxes);
    if (wahaList.length === 0) return;

    wahaRefreshesInFlight.add(accountId);
    try {
      await Promise.all(
        wahaList.map(async inbox => {
          try {
            await store.dispatch('inboxes/fetchWahaStatus', {
              accountId,
              inboxId: inbox.id,
            });
          } catch {
            // silent — per-inbox error, don't break the batch
          }
        })
      );
    } finally {
      wahaRefreshesInFlight.delete(accountId);
    }
  };

  return {
    disconnectedInboxes,
    hasDisconnectedInboxes,
    disconnectedCount,
    canManageInboxes,
    refreshInboxes,
    refreshWahaStatuses,
  };
}

export { inboxConnectionStatus, isInboxDisconnected };
